import React, { useState, useCallback, useMemo, useEffect, useRef } from 'react';
import { View, Text, StyleSheet, TouchableOpacity, ScrollView } from 'react-native';
import { IActivity, InteractiveStoryConfig, StoryNode, StoryNodeChoice, KeyStage, safeWorkpadConfig } from '@eduwrite/shared';
import { useStrokeStore } from '../store/useStrokeStore';
import { useAccessibilityStore } from '../store/useAccessibilityStore';
import { useWorkpadStore } from '../store/useWorkpadStore';
import { useFeatureFlags } from '../hooks/useFeatureFlags';
import { MultiModalInputArea } from './MultiModalInputArea';
import { AudioPromptButton } from './AudioPromptButton';

interface InteractiveStoryActivityProps {
    activity: IActivity;
    onComplete: () => void;
    keyStage?: KeyStage;
}

/**
 * InteractiveStoryActivity — branching narrative with tap choices.
 *
 * Flow:
 *   1. Display the current story node text.
 *   2. Show large tap-to-choose buttons for each choice.
 *   3. If a choice has a `writingPrompt`, show a handwriting canvas.
 *   4. On terminal node, the student taps "Finish" to complete.
 */
const DEFAULT_CHOICE_ICONS = ['🔵', '🟣', '🟡', '🟠'];

function shuffleChoices<T>(items: T[], seed: string): T[] {
    if (items.length <= 1) return items;
    const result = [...items];
    let hash = 0;
    for (let i = 0; i < seed.length; i++) {
        hash = ((hash << 5) - hash) + seed.charCodeAt(i);
        hash |= 0;
    }
    for (let i = result.length - 1; i > 0; i--) {
        hash = ((hash << 5) - hash) + i;
        hash |= 0;
        const j = Math.abs(hash) % (i + 1);
        [result[i], result[j]] = [result[j], result[i]];
    }
    return result;
}

// WP-028: Key-stage-appropriate hesitation delays before showing a hint
const HINT_DELAY_MS: Record<string, number> = {
    EYFS: 15000,
    KS1: 12000,
    KS2: 8000,
};
const DEFAULT_HINT_DELAY_MS = 12000;

export const InteractiveStoryActivity: React.FC<InteractiveStoryActivityProps> = ({ activity, onComplete, keyStage }) => {
    const isEYFS = keyStage === 'EYFS';
    const isKS2 = keyStage === 'KS2';
    const config = activity.config as InteractiveStoryConfig;
    const nodes: StoryNode[] = config?.nodes || [];
    const startNodeId: string = config?.startNodeId || (nodes[0]?.id ?? '');

    const [currentNodeId, setCurrentNodeId] = useState<string>(startNodeId);
    const [writingPrompt, setWritingPrompt] = useState<string | null>(null);
    const [history, setHistory] = useState<string[]>([]);
    const [wordBankOpen, setWordBankOpen] = useState(false);
    const [hasInput, setHasInput] = useState(false);

    // WP-028: Hint timer state
    const [hintVisible, setHintVisible] = useState(false);
    const hintTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

    // WP-028: Guided assist + deferred navigation state machine
    const [pendingNodeId, setPendingNodeId] = useState<string | null>(null);
    const [pendingWritingPrompt, setPendingWritingPrompt] = useState<string | null>(null);
    const [scaffoldPending, setScaffoldPending] = useState(false); // true while workpad is open for a guided choice

    const { completedStrokes, clearStrokes: _clearStrokes } = useStrokeStore();
    const highContrast = useAccessibilityStore((s) => s.highContrast);
    const workpadOpen = useWorkpadStore((s) => s.isOpen);
    const openWorkpad = useWorkpadStore((s) => s.open);
    const flags = useFeatureFlags();

    // EYFS: truncate story text to first 2 sentences
    const displayStoryText = (text: string): string => {
        if (!isEYFS) return text;
        const sentences = text.split(/[.!?]+/).filter(s => s.trim().length > 0);
        if (sentences.length <= 2) return text;
        return sentences.slice(0, 2).join('. ') + '.';
    };

    const currentNode = nodes.find(n => n.id === currentNodeId);
    const isTerminal = currentNode?.isTerminal || currentNode?.choices.length === 0;

    const shuffledChoices = useMemo(
        () => currentNode ? shuffleChoices(currentNode.choices, currentNode.id) : [],
        [currentNode?.id]
    );

    // WP-028: Clear hint timer helper
    const clearHintTimer = useCallback(() => {
        if (hintTimerRef.current) {
            clearTimeout(hintTimerRef.current);
            hintTimerRef.current = null;
        }
        setHintVisible(false);
    }, []);

    // WP-028: Start hint timer when entering a node with hintText
    useEffect(() => {
        clearHintTimer();
        if (
            currentNode?.hintText?.trim() &&
            !writingPrompt &&
            !workpadOpen
        ) {
            const delay = HINT_DELAY_MS[keyStage || ''] ?? DEFAULT_HINT_DELAY_MS;
            hintTimerRef.current = setTimeout(() => {
                setHintVisible(true);
            }, delay);
        }
        return () => {
            if (hintTimerRef.current) {
                clearTimeout(hintTimerRef.current);
                hintTimerRef.current = null;
            }
        };
    }, [currentNodeId, currentNode?.hintText, writingPrompt, workpadOpen, keyStage, clearHintTimer]);

    // WP-028: When workpad closes, resume deferred navigation from a guided choice.
    // Uses a setTimeout(0) to defer the check to after all state updates have been
    // processed, avoiding race conditions with React's batched updates.
    useEffect(() => {
        if (!workpadOpen && scaffoldPending && pendingNodeId) {
            const timer = setTimeout(() => {
                setScaffoldPending(false);
                if (pendingWritingPrompt) {
                    setWritingPrompt(pendingWritingPrompt);
                    setPendingWritingPrompt(null);
                } else {
                    setHistory(prev => [...prev, currentNodeId]);
                    setCurrentNodeId(pendingNodeId);
                    setHasInput(false);
                }
                setPendingNodeId(null);
            }, 0);
            return () => clearTimeout(timer);
        }
    }, [workpadOpen, scaffoldPending, pendingNodeId, pendingWritingPrompt, currentNodeId]);

    // WP-028: Navigate to a node (shared helper)
    const navigateToNode = useCallback((nextNodeId: string) => {
        setHistory(prev => [...prev, currentNodeId]);
        setCurrentNodeId(nextNodeId);
        setHasInput(false);
        setWritingPrompt(null);
    }, [currentNodeId]);

    const handleChoice = useCallback((choice: StoryNodeChoice) => {
        clearHintTimer();

        // WP-028: If choice has guidedAssist and workpad flag is on, open scaffold and defer navigation
        const guidedConfig = choice.guidedAssist ? safeWorkpadConfig(choice.guidedAssist) : null;
        if (guidedConfig && guidedConfig.enabled && flags.workpad) {
            setPendingNodeId(choice.nextNodeId);
            setPendingWritingPrompt(choice.writingPrompt || null);
            setScaffoldPending(true);
            openWorkpad(guidedConfig);
            return;
        }

        // No guided assist — navigate (with optional writing prompt) immediately
        if (choice.writingPrompt) {
            setPendingNodeId(choice.nextNodeId);
            setWritingPrompt(choice.writingPrompt);
        } else {
            navigateToNode(choice.nextNodeId);
        }
    }, [clearHintTimer, flags.workpad, openWorkpad, navigateToNode]);

    const handleBack = useCallback(() => {
        clearHintTimer();
        if (history.length > 0) {
            const prev = history[history.length - 1];
            setHistory(h => h.slice(0, -1));
            setCurrentNodeId(prev);
            setWritingPrompt(null);
            setHasInput(false);
            setPendingNodeId(null);
            setPendingWritingPrompt(null);
            setScaffoldPending(false);
        }
    }, [history, clearHintTimer]);

    const handleFinish = () => {
        if (writingPrompt && !hasInput && completedStrokes.length === 0) return;
        onComplete();
    };

    // WP-028: When writing prompt is submitted (Continue), navigate to pending node
    const handleWritingPromptContinue = () => {
        setWritingPrompt(null);
        if (pendingNodeId) {
            navigateToNode(pendingNodeId);
            setPendingNodeId(null);
        }
    };

    // If we have an active writing prompt, show multi-modal input (keyboard default, canvas toggle)
    if (writingPrompt && currentNode) {
        const canContinue = hasInput || completedStrokes.length > 0;
        return (
            <View style={styles.container}>
                <ScrollView style={styles.scrollArea} contentContainerStyle={styles.scrollContent}>
                    <View style={styles.header}>
                        <Text style={styles.title} allowFontScaling={true} maxFontSizeMultiplier={1.5}>{activity.title}</Text>
                        {activity.instructions && <Text style={styles.instructions} allowFontScaling={true} maxFontSizeMultiplier={1.5}>{activity.instructions}</Text>}
                    </View>
                    <View style={styles.promptBox}>
                        <Text style={styles.promptLabel} allowFontScaling={true} maxFontSizeMultiplier={1.5}>✏️ Write your answer:</Text>
                        <Text style={styles.promptText} allowFontScaling={true} maxFontSizeMultiplier={1.5}>{writingPrompt}</Text>
                    </View>
                </ScrollView>
                <View style={styles.canvasArea}>
                    <MultiModalInputArea
                        allowedModes={['keyboard', 'canvas']}
                        defaultMode="keyboard"
                        placeholder="Type your answer here..."
                        onChange={(value) => setHasInput(value.trim().length > 0)}
                    />
                </View>
                <View style={styles.footer}>
                    <TouchableOpacity style={styles.backBtn} onPress={handleBack}
                        accessibilityRole="button"
                        accessibilityLabel="Back"
                        accessibilityHint="Goes back to the previous story page"
                    >
                        <Text style={styles.backBtnText} allowFontScaling={true} maxFontSizeMultiplier={1.5}>Back</Text>
                    </TouchableOpacity>
                    <TouchableOpacity
                        style={[styles.submitBtn, !canContinue && styles.disabledBtn]}
                        onPress={handleWritingPromptContinue}
                        disabled={!canContinue}
                        accessibilityRole="button"
                        accessibilityLabel="Continue"
                        accessibilityHint="Continues to the next part of the story"
                        accessibilityState={{ disabled: !canContinue }}
                    >
                        <Text style={styles.submitBtnText} allowFontScaling={true} maxFontSizeMultiplier={1.5}>Continue ➔</Text>
                    </TouchableOpacity>
                </View>
            </View>
        );
    }

    return (
        <View style={[styles.container, highContrast && styles.containerHighContrast]}>
            <ScrollView style={styles.scrollArea} contentContainerStyle={styles.scrollContent}>
                <View style={styles.header}>
                    <Text style={[styles.title, highContrast && styles.titleHighContrast]} allowFontScaling={true} maxFontSizeMultiplier={1.5}>{activity.title}</Text>
                    {activity.instructions && <Text style={[styles.instructions, highContrast && styles.instructionsHighContrast]} allowFontScaling={true} maxFontSizeMultiplier={1.5}>{activity.instructions}</Text>}
                </View>

                {/* Story text card */}
                {currentNode && (
                    <View style={[styles.storyCard, highContrast && styles.storyCardHighContrast]}>
                        <Text style={[styles.storyText, isKS2 && styles.storyTextCompact, highContrast && styles.storyTextHighContrast]} allowFontScaling={true} maxFontSizeMultiplier={1.5}>
                            {displayStoryText(currentNode.text)}
                        </Text>
                    </View>
                )}

                {/* WP-028: Hesitation hint banner */}
                {hintVisible && currentNode?.hintText && (
                    <View
                        testID="story-hint-banner"
                        style={[styles.hintBanner, highContrast && styles.hintBannerHighContrast]}
                    >
                        <View style={styles.hintBannerContent}>
                            <Text style={[styles.hintBannerText, highContrast && styles.hintBannerTextHighContrast]} allowFontScaling={true} maxFontSizeMultiplier={1.5}>
                                {currentNode.hintText}
                            </Text>
                            {isEYFS && (
                                <AudioPromptButton
                                    text={currentNode.hintText}
                                    audioUrl={currentNode.hintAudioUrl}
                                />
                            )}
                        </View>
                        <TouchableOpacity
                            testID="story-hint-dismiss"
                            onPress={() => setHintVisible(false)}
                            style={styles.hintDismissBtn}
                            accessibilityRole="button"
                            accessibilityLabel="Dismiss hint"
                            accessibilityHint="Hides the hint"
                        >
                            <Text style={styles.hintDismissText} allowFontScaling={true} maxFontSizeMultiplier={1.5}>✕</Text>
                        </TouchableOpacity>
                    </View>
                )}

                {/* KS2 Word Bank drawer */}
                {isKS2 && currentNode?.vocabulary && currentNode.vocabulary.length > 0 && (
                    <View>
                        <TouchableOpacity style={[styles.wordBankToggle, highContrast && styles.wordBankToggleHighContrast]} onPress={() => setWordBankOpen(o => !o)}
                            accessibilityRole="button"
                            accessibilityLabel={wordBankOpen ? 'Hide Word Bank' : 'Show Word Bank'}
                        >
                            <Text style={[styles.wordBankToggleText, highContrast && styles.wordBankToggleTextHighContrast]} allowFontScaling={true} maxFontSizeMultiplier={1.5}>
                                {wordBankOpen ? '▲ Hide Word Bank' : '▼ Show Word Bank'}
                            </Text>
                        </TouchableOpacity>
                        {wordBankOpen && (
                            <View style={[styles.wordBankContainer, highContrast && styles.wordBankContainerHighContrast]}>
                                {currentNode.vocabulary.map((word, idx) => (
                                    <View key={idx} style={[styles.wordBankChip, highContrast && styles.wordBankChipHighContrast]}>
                                        <Text style={[styles.wordBankChipText, highContrast && styles.wordBankChipTextHighContrast]} allowFontScaling={true} maxFontSizeMultiplier={1.5}>{word}</Text>
                                    </View>
                                ))}
                            </View>
                        )}
                    </View>
                )}

                {/* Choice buttons */}
                {currentNode && shuffledChoices.length > 0 && (
                    <View style={styles.choicesContainer}>
                        {shuffledChoices.map((choice, idx: number) => (
                            <TouchableOpacity
                                key={idx}
                                style={[styles.choiceButton, isEYFS && styles.choiceButtonEYFS, highContrast && styles.choiceButtonHighContrast]}
                                onPress={() => handleChoice(choice)}
                                accessibilityRole="button"
                                accessibilityLabel={`Choice: ${choice.label}`}
                            >
                                {isEYFS && (
                                    <Text style={styles.choiceIcon} allowFontScaling={true} maxFontSizeMultiplier={1.5}>{choice.icon || DEFAULT_CHOICE_ICONS[idx % DEFAULT_CHOICE_ICONS.length]}</Text>
                                )}
                                <Text style={[styles.choiceButtonText, isKS2 && styles.choiceButtonTextCompact, highContrast && styles.choiceButtonTextHighContrast]} allowFontScaling={true} maxFontSizeMultiplier={1.5}>
                                    {choice.label}
                                </Text>
                            </TouchableOpacity>
                        ))}
                    </View>
                )}

                {/* Terminal node finish */}
                {isTerminal && (
                    <TouchableOpacity style={styles.finishButton} onPress={handleFinish}
                        accessibilityRole="button"
                        accessibilityLabel="Finish"
                        accessibilityHint="Completes the story activity"
                    >
                        <Text style={styles.finishButtonText} allowFontScaling={true} maxFontSizeMultiplier={1.5}>Finish ⭐</Text>
                    </TouchableOpacity>
                )}
            </ScrollView>
        </View>
    );
};

const styles = StyleSheet.create({
    container: { flex: 1, backgroundColor: '#FFFFFF', borderRadius: 16 },
    containerHighContrast: { backgroundColor: '#000000', borderColor: '#FFFF00', borderWidth: 2 },

    scrollArea: { flex: 1 },
    scrollContent: { padding: 20, gap: 16 },

    header: { marginBottom: 4 },
    title: { fontSize: 22, fontWeight: 'bold', color: '#1E293B', marginBottom: 4 },
    titleHighContrast: { color: '#FFFFFF' },
    instructions: { fontSize: 15, color: '#64748B', fontStyle: 'italic' },
    instructionsHighContrast: { color: '#FFFF00' },

    storyCard: {
        backgroundColor: '#F0FDF4',
        borderRadius: 16,
        padding: 20,
        borderLeftWidth: 6,
        borderLeftColor: '#22C55E',
    },
    storyCardHighContrast: {
        backgroundColor: '#000000',
        borderLeftColor: '#FFFF00',
        borderColor: '#FFFF00',
        borderWidth: 2,
    },
    storyText: { fontSize: 18, color: '#1E293B', lineHeight: 28, fontWeight: '500' },
    storyTextHighContrast: { color: '#FFFFFF' },

    choicesContainer: { gap: 12, marginTop: 8 },
    choiceButton: {
        backgroundColor: '#EEF2FF',
        borderRadius: 14,
        paddingVertical: 18,
        paddingHorizontal: 20,
        borderWidth: 2,
        borderColor: '#C7D2FE',
        alignItems: 'center',
    },
    choiceButtonHighContrast: {
        backgroundColor: '#000000',
        borderColor: '#FFFF00',
    },
    choiceButtonText: { fontSize: 17, fontWeight: '700', color: '#4338CA' },
    choiceButtonTextHighContrast: { color: '#FFFF00' },

    finishButton: {
        backgroundColor: '#22C55E',
        borderRadius: 16,
        paddingVertical: 18,
        alignItems: 'center',
        shadowColor: '#22C55E',
        shadowOffset: { width: 0, height: 4 },
        shadowOpacity: 0.3,
        shadowRadius: 8,
        elevation: 5,
    },
    finishButtonText: { fontSize: 20, fontWeight: '900', color: '#fff' },

    promptBox: {
        backgroundColor: '#EEF2FF',
        borderRadius: 12,
        borderLeftWidth: 4,
        borderLeftColor: '#6366F1',
        padding: 14,
    },
    promptLabel: { fontSize: 12, fontWeight: 'bold', color: '#6366F1', marginBottom: 4, letterSpacing: 0.5 },
    promptText: { fontSize: 16, color: '#1E293B', lineHeight: 24 },

    canvasArea: { flex: 1, marginHorizontal: 16, marginBottom: 8, borderRadius: 12, overflow: 'hidden', backgroundColor: '#FAFAFA' },

    footer: {
        flexDirection: 'row', paddingHorizontal: 16, paddingVertical: 12, gap: 12,
        borderTopWidth: 1, borderTopColor: '#F1F5F9',
    },
    backBtn: {
        flex: 1, height: 56, minHeight: 56, borderRadius: 10, borderWidth: 1, borderColor: '#CBD5E1',
        alignItems: 'center', justifyContent: 'center',
    },
    backBtnText: { fontSize: 15, fontWeight: '600', color: '#64748B' },
    submitBtn: {
        flex: 3,
        height: 64,
        backgroundColor: '#22c55e',
        borderRadius: 16,
        alignItems: 'center',
        justifyContent: 'center',
        shadowColor: '#22c55e',
        shadowOffset: { width: 0, height: 4 },
        shadowOpacity: 0.3,
        shadowRadius: 8,
        elevation: 5,
    },
    disabledBtn: { backgroundColor: '#D1D5DB' },
    submitBtnText: { fontSize: 22, fontWeight: '900', color: '#fff' },

    // KS2 compact text
    storyTextCompact: { fontSize: 15, lineHeight: 22 },

    // EYFS large choice buttons with icon support
    choiceButtonEYFS: {
        flexDirection: 'row',
        gap: 10,
        paddingVertical: 22,
        paddingHorizontal: 24,
    },
    choiceIcon: { fontSize: 28 },

    // KS2 compact choice buttons
    choiceButtonTextCompact: { fontSize: 15 },

    // KS2 Word Bank drawer
    wordBankToggle: {
        backgroundColor: '#F1F5F9',
        borderRadius: 10,
        paddingVertical: 10,
        paddingHorizontal: 14,
        alignSelf: 'flex-start',
    },
    wordBankToggleHighContrast: {
        backgroundColor: '#000000',
        borderColor: '#FFFF00',
        borderWidth: 2,
    },
    wordBankToggleText: { fontSize: 14, fontWeight: '700', color: '#475569' },
    wordBankToggleTextHighContrast: { color: '#FFFF00' },
    wordBankContainer: {
        flexDirection: 'row',
        flexWrap: 'wrap',
        gap: 8,
        marginTop: 8,
        padding: 12,
        backgroundColor: '#F8FAFC',
        borderRadius: 10,
    },
    wordBankContainerHighContrast: {
        backgroundColor: '#000000',
        borderColor: '#FFFF00',
        borderWidth: 2,
    },
    wordBankChip: {
        backgroundColor: '#E0F2FE',
        borderRadius: 20,
        paddingHorizontal: 12,
        paddingVertical: 6,
    },
    wordBankChipHighContrast: {
        backgroundColor: '#000000',
        borderColor: '#FFFF00',
        borderWidth: 2,
    },
    wordBankChipText: { fontSize: 14, fontWeight: '600', color: '#0284C7' },
    wordBankChipTextHighContrast: { color: '#FFFF00' },

    // WP-028: Hesitation hint banner
    hintBanner: {
        flexDirection: 'row',
        alignItems: 'center',
        backgroundColor: '#FEF3C7',
        borderRadius: 12,
        paddingVertical: 14,
        paddingHorizontal: 16,
        borderLeftWidth: 4,
        borderLeftColor: '#F59E0B',
        gap: 8,
    },
    hintBannerHighContrast: {
        backgroundColor: '#000000',
        borderColor: '#FFFF00',
        borderLeftColor: '#FFFF00',
        borderWidth: 2,
    },
    hintBannerContent: {
        flex: 1,
        flexDirection: 'column',
        gap: 8,
    },
    hintBannerText: {
        fontSize: 16,
        fontWeight: '600',
        color: '#92400E',
        lineHeight: 24,
    },
    hintBannerTextHighContrast: {
        color: '#FFFF00',
    },
    hintDismissBtn: {
        width: 56,
        height: 56,
        minHeight: 56,
        minWidth: 56,
        borderRadius: 28,
        backgroundColor: 'rgba(146, 64, 14, 0.15)',
        alignItems: 'center',
        justifyContent: 'center',
    },
    hintDismissText: {
        fontSize: 18,
        fontWeight: '700',
        color: '#92400E',
    },
});
